/*
 * Autoplaying process videos.
 *
 * The markup ships with `autoplay`, so a clip still plays if this script never
 * runs. What this adds:
 *
 *  - `preload="none"` means nothing downloads until the clip is near the
 *    viewport; an IntersectionObserver starts it there and pauses it again
 *    once it scrolls away, so four clips do not sit decoding off screen.
 *  - A pause control, because motion that auto-starts and runs longer than
 *    five seconds needs a way to stop it (WCAG 2.2.2).
 *  - Reduced-motion visitors get a poster frame and normal controls instead of
 *    autoplay.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {HTMLVideoElement} video */
function honourReducedMotion(video) {
  video.removeAttribute('autoplay');
  video.autoplay = false;
  video.controls = true;
  video.pause();
  video.closest('.production-showcase__media')?.querySelector('[data-ip-video-toggle]')?.remove();
}

/**
 * @param {HTMLVideoElement} video
 * @param {HTMLButtonElement | null} toggle
 */
function wireToggle(video, toggle) {
  if (!toggle) return;

  const label = toggle.querySelector('.production-showcase__toggle-label');

  const sync = () => {
    const paused = video.paused;
    toggle.setAttribute('aria-pressed', String(paused));
    if (label) label.textContent = paused ? 'Play' : 'Pause';
  };

  toggle.addEventListener('click', () => {
    if (video.paused) {
      // A manual play should stick even if the clip scrolls out of view.
      video.dataset.ipUserPaused = 'false';
      video.play().catch(() => {});
    } else {
      video.dataset.ipUserPaused = 'true';
      video.pause();
    }
    sync();
  });

  video.addEventListener('play', sync);
  video.addEventListener('pause', sync);
  sync();
}

function init() {
  /** @type {NodeListOf<HTMLVideoElement>} */
  const videos = document.querySelectorAll('.production-showcase__video--auto');
  if (!videos.length) return;

  videos.forEach((video) => {
    const toggle = /** @type {HTMLButtonElement | null} */ (
      video.closest('.production-showcase__media')?.querySelector('[data-ip-video-toggle]') ?? null
    );

    if (REDUCED_MOTION.matches) {
      honourReducedMotion(video);
      return;
    }

    // Muted is what makes autoplay permissible; never rely on the attribute alone.
    video.muted = true;
    wireToggle(video, toggle);
  });

  if (REDUCED_MOTION.matches) return;

  if (!('IntersectionObserver' in window)) {
    videos.forEach((video) => video.play().catch(() => {}));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = /** @type {HTMLVideoElement} */ (entry.target);
        if (entry.isIntersecting) {
          if (video.dataset.ipUserPaused === 'true') return;
          if (video.preload === 'none') video.preload = 'auto';
          video.play().catch(() => {
            // Autoplay refused (low power mode, data saver): leave the poster
            // showing and let the toggle act as a play button.
            video.controls = false;
          });
        } else if (!video.paused) {
          video.pause();
        }
      });
    },
    { rootMargin: '200px 0px', threshold: 0.25 }
  );

  videos.forEach((video) => observer.observe(video));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
